import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Image } from '@asigna/kit';
import cx from 'classnames';
import { NotificationCard, SkeletonSafe } from 'components';
import useAddress from 'hooks/use-address';
import useNetwork from 'hooks/use-network';
import EmptyIcon from 'images/empty_icon.png';
import { useAtom } from 'jotai';
import { toast } from 'sonner';
import { createdSafeAtom, useArchivedSafes, usePendingSafes, useSafes } from 'store';
import { SuccessModalType } from 'types';

import plusIcon from './util/images/plus.svg';
import { MultisigSafe, PendingSafeCard } from './components';

const tabs = ['Active Vaults', 'Archived'] as const;

type Tabs = (typeof tabs)[number];

export const TreasuryPage = () => {
  const history = useHistory();
  const walletAccount = useAddress();
  const [network] = useNetwork();
  const [createSafe] = useAtom(createdSafeAtom);

  const [selectedTab, setSelectedTab] = useState<Tabs>('Active Vaults');
  const { safes, isSafesLoading } = useSafes();
  const { pendingSafes } = usePendingSafes();
  const { archivedSafes } = useArchivedSafes();
  const isLoading = isSafesLoading;
  // && !(safes.length || pendingSafes?.length || archivedSafes.length);
  const openStatusModal = useCallback(
    (modalType: SuccessModalType, name?: string) => {
      switch (modalType) {
        // case SuccessModalType.INVITE_ACCEPTED:
        //   break;
        case SuccessModalType.SAFE_CREATED:
        case SuccessModalType.SAFE_MULTIPLE_OWNER_CREATED:
          toast(
            () => (
              <NotificationCard
                title="Vault created"
                message={`Vault <b>${name}</b> was successfully created.`}
                type="success"
              />
            ),
            {
              className: 'w-full',
            }
          );
          break;
      }
    },
    [network]
  );

  useEffect(() => {
    if (createSafe) {
      openStatusModal(createSafe?.option, createSafe?.name);
    }
  }, [createSafe, openStatusModal]);

  const parentSafes = useMemo(() => {
    return safes.filter((safe) => !safe.parrentId || safe.parrentId === safe?._id);
  }, [safes]);

  // const handleVerify = (item: IMultiSignWallet) => {
  //   const searchParams = new URLSearchParams();
  //   searchParams.append(
  //     'publicKeys',
  //     encodeURIComponent(JSON.stringify(item.owners.map((owner) => owner.publicKey)))
  //   );
  //   searchParams.append('network', network);
  //   searchParams.append('address', item.address);
  //   searchParams.append('threshold', item.threshold.toString());
  //   searchParams.append('chain', 'STX');
  //   window.open('https://verify-address.dev.asigna.io/?' + searchParams.toString());
  // };

  return (
    <div className="w-full h-[100vh] overflow-y-scroll pb-80 px-20">
      <div className="flex flex-col gap-16 sm:gap-32 mx-auto max-w-[1352px] pt-40">
        {(isLoading || safes.length > 0 || pendingSafes.length > 0 || archivedSafes.length > 0) && (
          <div className="flex items-start justify-between gap-8 flex-wrap">
            <p className="text-r24 text-white leading-[36px]">Your Vaults</p>
            {archivedSafes.length > 0 && (
              <div className="flex gap-8">
                {tabs.map((tab) => {
                  return (
                    <button
                      onClick={() => setSelectedTab(tab)}
                      className={cx(
                        'px-20 py-4 border rounded-[40px] h-36 text-r13',
                        tab === selectedTab
                          ? 'bg-primary-10 border-primary text-white hover:bg-primary-20'
                          : 'bg-black-2 border-white-10 text-regular hover:bg-white-10'
                      )}
                      key={tab}
                    >
                      {tab}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {isLoading && <SkeletonSafe />}
        {!isLoading &&
          (safes.length > 0 || pendingSafes.length > 0 || archivedSafes.length > 0) && (
            <>
              {selectedTab === 'Active Vaults' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-16 lg:gap-24 xl:gap-32 flex-wrap">
                  <button
                    onClick={() => {
                      history.push('/createSafe');
                    }}
                    className="flex flex-col justify-center items-center gap-8 p-16 sm:p-24 rounded-[12px] border-primary border-[0.5px] border-dashed bg-primary-5 hover:bg-primary-10 min-h-[196px] transition-all"
                  >
                    <div className="flex items-center justify-center rounded-[50%] w-32 h-32 bg-[linear-gradient(90deg,_#584BEA_0%,_#905CFF_100%)]">
                      <Image src={plusIcon} size={14} />
                    </div>
                    <p className="text-m15 text-white">Create New Vault</p>
                  </button>
                  {pendingSafes &&
                    pendingSafes.map((pendingSafe) => (
                      <PendingSafeCard
                        key={pendingSafe._id}
                        pendingSafe={pendingSafe}
                        addressWallet={walletAccount || ''}
                      />
                    ))}

                  {parentSafes.map((safe, index) => (
                    <MultisigSafe key={safe._id} safe={safe} index={index} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-16 lg:gap-24 xl:gap-32 flex-wrap">
                  {archivedSafes.map((safe) => (
                    <MultisigSafe key={safe._id} safe={safe} />
                  ))}
                </div>
              )}
            </>
          )}
        {!isLoading && safes.length < 1 && pendingSafes.length < 1 && archivedSafes.length < 1 && (
          <div className="flex flex-col items-center justify-center gap-20 mt-40">
            <Image className="mb-4" size={100} src={EmptyIcon} alt="Welcome to Asigna" />
            <p className="text-r24 text-white">Welcome to Asigna</p>
            <Button
              colorType="primary"
              onClick={() => history.push('/createSafe')}
              message="Create New Vault"
              className="min-w-[280px] mb-80"
            />
          </div>
        )}
      </div>
    </div>
  );
};
